import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { buscarUsuarioPorCedula } from '../../../../common/services/api';

// ==============================================================
// COMPONENTE DE FIRMA (Reutilizable)
// ==============================================================
function FirmaPad({ onSave, onClear, titulo = "Firma del Responsable" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-2 w-full mt-2">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full h-[140px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-2 left-3 text-[9px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          {titulo}
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[9px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-3 py-1 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

// ==============================================================
// MODAL DE ALERTAS
// ==============================================================
function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

// ==============================================================
// CATÁLOGOS OFICIALES
// ==============================================================
const ESTADOS_CIVILES = ["Soltero(a)", "Casado(a)", "Unión Libre", "Separado(a)", "Viudo(a)"];
const TIPO_RETIRO = [
  "No aplica (Reporte de rutina)",
  "Se retira por sus propios medios",
  "Acompañado por un compañero",
  "Acompañado por un familiar",
  "Trasladado en servicio médico"
];

// ==============================================================
// COMPONENTE PRINCIPAL
// ==============================================================
export default function AutoreporteSaludSST({ usuario, onBack }) {
  
  const [datos, setDatos] = useState({
    ciudad: "Fusagasugá",
    fecha: new Date().toISOString().split('T')[0],
    identificacion: "",
    nombres: "",
    cargo: "",
    pdv: "",
    sexo: "",
    edad: "",
    estadoCivil: "",
    eps: "",
    afp: "",
    arl: "SURA",
    retiroTrabajo: "No aplica (Reporte de rutina)",
    presentaEnfermedad: "",
    sintomas: "",
    relacionTrabajo: "",
    relacionFuera: "",
    sugerencias: ""
  });

  // Estado para las sedes traídas de MySQL
  const [listaSedes, setListaSedes] = useState([]);
  const [firmaColaborador, setFirmaColaborador] = useState(null);
  const [firmaResponsable, setFirmaResponsable] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [buscandoDocumento, setBuscandoDocumento] = useState(false);

  const handleChange = (e) => setDatos({ ...datos, [e.target.name]: e.target.value });

  // Cargar sedes dinámicas desde la base de datos al montar el componente
  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${API_URL}/api/sedes`);
        const data = await res.json();
        if (data.success && Array.isArray(data.sedes)) {
          setListaSedes(data.sedes);
        }
      } catch (error) {
        console.error("Error al cargar sedes maestras:", error);
      }
    };
    fetchSedes();
  }, []);

  // Búsqueda Automática
  useEffect(() => {
    const docBuscado = datos.identificacion.trim();
    if (!docBuscado) {
      setDatos(prev => ({ ...prev, nombres: "", cargo: "", pdv: "" }));
      setBuscandoDocumento(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setBuscandoDocumento(true);
      try {
        const resultados = await buscarUsuarioPorCedula(docBuscado);
        console.log("Respuesta del backend:", resultados);
        if (resultados && resultados.length > 0) {
          const u = resultados[0];
          const nombreCompleto = u.terc_nom || (u.nombres && u.apellidos ? `${u.nombres} ${u.apellidos}` : "") || u.nombre || "";
          const cargoOficial = u.empl_cargo || u.cargo || "";
          const sedeRaw = u.area_nombre || u.centroTrabajo || u.sede || u.pdv || "";
          
          // Coincidencia con la sede maestra cargada de la BD
          const sedeEncontrada = listaSedes.find(s => s.nombre.toLowerCase() === sedeRaw.toLowerCase());
          const sedeNormalizada = sedeEncontrada ? sedeEncontrada.nombre : sedeRaw;

          setDatos(prev => ({
            ...prev,
            nombres: nombreCompleto.trim(),
            cargo: cargoOficial.trim(),
            pdv: sedeNormalizada
          }));
        } else {
          setDatos(prev => ({ ...prev, nombres: "", cargo: "", pdv: "" }));
        }
      } catch (error) {
        console.error("Error al buscar empleado automático:", error);
      } finally {
        setBuscandoDocumento(false);
      }
    }, 600); 

    return () => clearTimeout(delayDebounceFn);
  }, [datos.identificacion, listaSedes]);

  const handleSubmit = async () => {
    if (!datos.identificacion || !datos.nombres || !datos.pdv || !datos.sintomas.trim() || !firmaColaborador) {
      return setCustomAlert({ show: true, message: "Por favor, completa los campos obligatorios y firma el reporte.", type: "warning" });
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        tipo_formulario: "AUTOREPORTE_SALUD",
        datosGenerales: { 
          pdv: datos.pdv, 
          fecha: datos.fecha, 
          colaborador: datos.nombres,
          evaluador: datos.nombres, 
          identificacion: datos.identificacion
        },
        respuestas: datos,
        observaciones: { sugerencias: datos.sugerencias },
        puntaje_global: 0,
        evidencias: { 
          firma_colaborador: firmaColaborador,
          firma_responsable: firmaResponsable 
        }
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "Autoreporte registrado exitosamente.", type: "success" });
        setTimeout(() => onBack(), 1500);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar.", type: "error" });
      }
    } catch (error) {
      setCustomAlert({ show: true, message: "Error de conexión con el servidor.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA */}
      <div className="bg-gradient-to-r from-blue-600/90 to-indigo-700/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-xl font-black text-white leading-tight">Autoreporte de Condiciones de Salud</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">Código: FT-SST-05 | Versión: 03</span>
        </div>
      </div>

      {/* ÁREA SCROLLABLE */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 relative custom-scrollbar">
        
        {/* SECCIÓN 1: DATOS DEL TRABAJADOR */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <h2 className="text-sm font-black text-indigo-900 uppercase tracking-tight mb-5 flex items-center gap-2">
            <span>👤</span> Datos del Trabajador
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-5">
            
            <div className="md:col-span-3">
              <label className="flex items-center gap-2 text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">
                Identificación * 
                {buscandoDocumento && <span className="text-[9px] text-indigo-500 animate-pulse lowercase font-bold tracking-normal">(buscando...)</span>}
              </label>
              <input 
                type="text" name="identificacion" value={datos.identificacion} onChange={handleChange} placeholder="Ej: 1098..."
                className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm transition-all m-0" 
              />
            </div>

            <div className="md:col-span-6">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Nombres y Apellidos *</label>
              <input 
                type="text" name="nombres" value={datos.nombres} readOnly placeholder="Se carga al escribir..."
                className="w-full h-[40px] bg-slate-100/80 border-transparent rounded-xl px-3 text-xs font-bold text-slate-600 outline-none shadow-inner cursor-not-allowed m-0" 
              />
            </div>

            {/* Selector dinámico conectado a la tabla maestra de sedes con código de bodega */}
            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Sede (PDV) *</label>
              <select name="pdv" value={datos.pdv} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="" disabled>-- Seleccione --</option>
                {listaSedes.map(sede => (
                  <option key={sede.id} value={sede.nombre}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-6">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Cargo Oficial</label>
              <input 
                type="text" name="cargo" value={datos.cargo} readOnly placeholder="Traído de la tabla..."
                className="w-full h-[40px] bg-slate-100/80 border-transparent rounded-xl px-3 text-xs font-bold text-slate-600 outline-none shadow-inner cursor-not-allowed m-0" 
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Sexo</label>
              <select name="sexo" value={datos.sexo} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="">N/A</option>
                <option value="Masculino">M</option>
                <option value="Femenino">F</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Edad</label>
              <input type="number" name="edad" value={datos.edad} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Estado Civil</label>
              <select name="estadoCivil" value={datos.estadoCivil} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="">-- Seleccione --</option>
                {ESTADOS_CIVILES.map(est => <option key={est} value={est}>{est}</option>)}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">EPS</label>
              <input type="text" name="eps" value={datos.eps} onChange={handleChange} placeholder="Ej: Famisanar" className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">AFP (Pensión)</label>
              <input type="text" name="afp" value={datos.afp} onChange={handleChange} placeholder="Ej: Porvenir" className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">ARL</label>
              <input type="text" name="arl" value={datos.arl} onChange={handleChange} placeholder="SURA" className="w-full h-[40px] bg-white/80 border-transparent focus:border-indigo-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

          </div>
        </div>

        {/* SECCIÓN 2: CONDICIONES DE SALUD Y SÍNTOMAS */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <h2 className="text-sm font-black text-indigo-900 uppercase tracking-tight mb-4 flex items-center gap-2">
            <span>🩺</span> Cuestionario de Salud
          </h2>

          <div className="space-y-5">
            <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
              <label className="block text-xs font-bold text-slate-800 mb-3">1. ¿Actualmente presenta alguna enfermedad o está en tratamiento médico?</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="presentaEnfermedad" value="SI" checked={datos.presentaEnfermedad === 'SI'} onChange={handleChange} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-xs font-black text-slate-700">SÍ</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="presentaEnfermedad" value="NO" checked={datos.presentaEnfermedad === 'NO'} onChange={handleChange} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-xs font-black text-slate-700">NO</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-800 mb-2">2. Describa qué síntoma(s) físico(s) y/o situaciones psicosociales está presentando actualmente: *</label>
              <textarea 
                name="sintomas" value={datos.sintomas} onChange={handleChange} rows="3" 
                placeholder="Ej: Dolor en la espalda baja, dolor de cabeza constante..."
                className="w-full bg-white/80 border border-slate-300 focus:border-indigo-400 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 outline-none shadow-sm resize-none" 
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
                  <label className="block text-xs font-bold text-slate-800 mb-3">3. ¿Considera que estos síntomas tienen relación con su TRABAJO?</label>
                  <div className="flex gap-6">
                     <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="relacionTrabajo" value="SI" checked={datos.relacionTrabajo === 'SI'} onChange={handleChange} className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-black text-slate-700">SÍ</span>
                     </label>
                     <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="relacionTrabajo" value="NO" checked={datos.relacionTrabajo === 'NO'} onChange={handleChange} className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-black text-slate-700">NO</span>
                     </label>
                  </div>
               </div>
               <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
                  <label className="block text-xs font-bold text-slate-800 mb-3">4. ¿Considera que tienen relación con actividades FUERA del trabajo?</label>
                  <div className="flex gap-6">
                     <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="relacionFuera" value="SI" checked={datos.relacionFuera === 'SI'} onChange={handleChange} className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-black text-slate-700">SÍ</span>
                     </label>
                     <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="relacionFuera" value="NO" checked={datos.relacionFuera === 'NO'} onChange={handleChange} className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-black text-slate-700">NO</span>
                     </label>
                  </div>
               </div>
            </div>

            <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-200">
              <label className="block text-xs font-bold text-amber-900 mb-2">
                ⚠️ Constancia de retiro (Solo si el colaborador manifiesta malestar y se retira de las instalaciones)
              </label>
              <select name="retiroTrabajo" value={datos.retiroTrabajo} onChange={handleChange} className="w-full bg-white border border-amber-300 focus:border-amber-500 rounded-xl px-3 py-2 text-xs font-bold text-amber-900 outline-none shadow-sm cursor-pointer">
                {TIPO_RETIRO.map(tipo => <option key={tipo} value={tipo}>{tipo}</option>)}
              </select>
              {datos.retiroTrabajo !== "No aplica (Reporte de rutina)" && (
                <p className="mt-2 text-[10px] text-amber-700 italic">
                  Se deja constancia que el retiro se da por iniciativa del trabajador, quien decide consultar en su red de atención médica de forma consciente.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* SECCIÓN 3: RECOMENDACIONES Y FIRMAS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80 flex flex-col">
            <h2 className="text-sm font-black text-indigo-900 uppercase tracking-tight mb-3 flex items-center gap-2">
              <span>💡</span> Sugerencias
            </h2>
            <label className="block text-[10px] font-black text-slate-600 mb-2 uppercase">Sugerencias y recomendaciones para el mejoramiento de las condiciones de salud</label>
            <textarea 
              name="sugerencias" value={datos.sugerencias} onChange={handleChange} 
              className="w-full flex-1 min-h-[140px] bg-white/80 border border-slate-300 focus:border-indigo-400 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 outline-none shadow-sm resize-none" 
            />
          </div>

          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
            <h2 className="text-sm font-black text-indigo-900 uppercase tracking-tight mb-3 flex items-center gap-2">
              <span>✍️</span> Firmas
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <FirmaPad onSave={setFirmaColaborador} onClear={() => setFirmaColaborador(null)} titulo="Firma del Colaborador *" />
              <FirmaPad onSave={setFirmaResponsable} onClear={() => setFirmaResponsable(null)} titulo="Firma Responsable Área/PDV" />
            </div>
            <button onClick={handleSubmit} className="w-full mt-5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white font-black py-3.5 rounded-xl shadow-lg active:scale-95 transition-all text-xs tracking-widest uppercase cursor-pointer border border-indigo-400">
              Registrar Autoreporte
            </button>
          </div>
        </div>
      </div>

      {/* ALERTAS */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}